import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';

import { LoadingService } from '@services/loading.service';

const SHOW_DELAY = 180;
const MIN_VISIBLE = 320;

@Component({
  selector: 'app-loading-bar',
  template: `
    @if (visible()) {
      <div class="loading-bar" [class.loading-bar--settling]="!isLoading()" aria-hidden="true">
        <span class="loading-bar__indicator"></span>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoadingBar {
  private readonly loadingService = inject(LoadingService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isLoading = this.loadingService.isLoading;
  protected readonly visible = signal(false);

  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private shownAt = 0;

  constructor() {
    effect(() => {
      const loading = this.isLoading();
      untracked(() => (loading ? this.scheduleShow() : this.scheduleHide()));
    });

    this.destroyRef.onDestroy(() => {
      this.clearShowTimer();
      this.clearHideTimer();
    });
  }

  private scheduleShow(): void {
    this.clearHideTimer();

    if (this.visible() || this.showTimer) {
      return;
    }

    this.showTimer = setTimeout(() => {
      this.showTimer = null;
      this.shownAt = Date.now();
      this.visible.set(true);
    }, SHOW_DELAY);
  }

  private scheduleHide(): void {
    this.clearShowTimer();

    if (!this.visible() || this.hideTimer) {
      return;
    }

    const remaining = Math.max(0, MIN_VISIBLE - (Date.now() - this.shownAt));
    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      this.visible.set(false);
    }, remaining);
  }

  private clearShowTimer(): void {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
  }

  private clearHideTimer(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }
}
